import { Draggable } from '@hello-pangea/dnd';
import {
  DragIcon,
  EditIcon,
  ExpandIcon,
  LinkIcon,
  ThreeDots,
  TrashIcon,
} from 'assets/icons';
import { AudioPlayer } from 'common/audio-player';
import { AudioRecorder } from 'common/audio-recorder';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { CameraRecord } from 'common/camera-upload/components/camera-record';
import { CopyLink } from 'common/copy-link';
import { DatePicker } from 'common/date-picker';
import { DropDownListItem } from 'common/drop-down-list-item';
import {
  AlbumAddContentTypes,
  AlbumContentType,
  NotificationType,
} from 'common/enums';
import { HtmlRenderer } from 'common/html-renderer';
import { Menu, PositionType } from 'common/menu';
import { ModalWarning } from 'common/modal-warning';
import { RichTextEditor } from 'common/rich-text-editor';
import { Spinner } from 'common/spinner';
import { UserInitials } from 'common/user-initials';
import { apiBaseURL } from 'config/constants';
import { appActions, AppContext } from 'context/app-context';
import { createUrlToDownloadFile } from 'helpers/file-utils';
import { adaptContentType, classnames, getInitials } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { API_ROUTES } from 'networking/api-routes';
import { ApiService } from 'networking/api-service';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { lazy, Suspense, useContext, useRef, useState } from 'react';
import { RouteName } from 'routes';
import { getRouteFor } from 'routes/route-helpers';
import { ContentEntryTitle } from '../content-entry-title';
import styles from './content-entry.module.scss';

interface BaseProps {
  userName: string;
}

interface DisplayProps extends BaseProps {
  isCreatingNewContent?: false;
  content: AlbumContentSerialized;
  onDeleteContent: () => void;
  index: number;
  draggable?: boolean;
  editable?: boolean;
  isGuestView?: boolean;
  onShowViewMode: () => void;
  onContentUpdate?: (
    contentId: number,
    updatedJournalDocument: JournalDocumentType,
  ) => void;
  onContentNameUpdate?: (contentId: number, newName: string) => void;
}

interface CreateNewProps extends BaseProps {
  isCreatingNewContent: true;
  contentType: AlbumAddContentTypes;
  onStopRecording: () => void;
  onNewContentTitleChange?: (title: string) => void;
}

export type ContentEntryProps = DisplayProps | CreateNewProps;

const PdfViewer = lazy(() =>
  import('common/pdf-viewer').then((module) => ({ default: module.PdfViewer })),
);

export const ContentEntry = (props: ContentEntryProps) => {
  const { userName } = props;
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteContentModal, setShowDeleteContentModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newContentTitle, setNewContentTitle] = useState<string>('');
  const moreOptionsMenuRef = useRef<HTMLDivElement>(null);
  useClickOutside(moreOptionsMenuRef, () => setShowMenu(false));
  const { dispatch } = useContext(AppContext);

  const editable =
    props.isCreatingNewContent === true ? true : (props.editable ?? true);
  const isGuestView =
    props.isCreatingNewContent === true ? false : (props.isGuestView ?? false);

  const draggable =
    props.isCreatingNewContent === true ? false : (props.draggable ?? true);

  const contentType = props.isCreatingNewContent
    ? props.contentType
    : adaptContentType(props.content);

  const handleUpdateDate = props.isCreatingNewContent
    ? undefined
    : async (startDate: string, endDate: string) => {
        try {
          await VaultsController.updateContentMetadata(props.content.id, {
            startDate,
            endDate,
          });
          dispatch({
            type: appActions.NOTIFICATION,
            notification: new NotificationObject({
              show: true,
              title: 'Date updated',
              type: NotificationType.Success,
            }),
          });
        } catch (error) {
          dispatch({
            type: appActions.NOTIFICATION,
            notification: new NotificationObject({
              show: true,
              title: 'Failed to update date',
              type: NotificationType.Error,
            }),
          });
        }
      };

  const handleDeleteContent = async () => {
    if (props.isCreatingNewContent) return;
    setShowDeleteContentModal(false);
    try {
      await VaultsController.deleteAlbumContent(
        props.content.id,
        props.content.fileSize ?? 0,
      );
      props.onDeleteContent();
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Content deleted',
          type: NotificationType.Success,
        }),
      });
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Failed to delete content',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleDownloadEntry = async () => {
    if (props.isCreatingNewContent) return;
    setShowMenu(false);

    const { content, name } = props.content;

    try {
      let blob: Blob;
      let fileName: string;

      if (contentType === AlbumContentType.write) {
        const response = await ApiService.get<Blob>(
          API_ROUTES.DOWNLOAD_JOURNAL_ENTRY_DOCX(content.id),
          {},
          { responseType: 'blob' },
        );

        blob = response.data;
        fileName = `${name}.docx`;
      } else if (props.content.mediaUrl) {
        const downloadUrl = createUrlToDownloadFile(
          props.content.mediaUrl,
          name,
        );

        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Failed to download: ${response.statusText}`);
        }

        blob = await response.blob();
        fileName = name;
      } else {
        throw new Error('No file available for download');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Download failed',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to download the file. Please try again.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const modalDeleteContent = () => (
    <ModalWarning
      title="Delete content"
      content="Are you sure you want to delete this Entry? This action cannot be undone."
      successButtonText="Delete"
      successStyleButton={ButtonStyle.DestructivePrimary}
      closeFn={() => setShowDeleteContentModal(false)}
      successFn={handleDeleteContent}
    />
  );

  const contentComponent = (
    <div
      className={classnames(styles.container, {
        [styles.creating]: !!props.isCreatingNewContent,
      })}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {props.isCreatingNewContent ? (
            <ContentEntryTitle
              title={newContentTitle}
              isCreatingNew
              onTitleChange={(newName: string) => {
                setNewContentTitle(newName);
                props.onNewContentTitleChange?.(newName);
              }}
            />
          ) : (
            <ContentEntryTitle
              title={props.content.name}
              contentId={props.content.id}
              editable={editable}
              onTitleChange={(newName: string) => {
                props.onContentNameUpdate?.(props.content.id, newName);
              }}
            />
          )}
        </div>
        <div className={styles.headerRight}>
          {/* Author avatar is only meaningful when viewing an existing entry —
              hide it while creating a new one (you're the author). */}
          {!props.isCreatingNewContent && userName.length > 0 && (
            <div className={styles.userNameContainer}>
              <UserInitials
                name={getInitials(userName)}
                innerClassName={styles.smallUserInitials}
              />
            </div>
          )}
          {!props.isCreatingNewContent && !isGuestView && (
            <div className={styles.copyLinkContainer}>
              <CopyLink
                path={`${apiBaseURL}${getRouteFor(RouteName.ContentViewMode, {
                  contentId: props.content.id,
                })}`}
              >
                <LinkIcon />
                <span>Copy link</span>
              </CopyLink>
            </div>
          )}
          {!props.isCreatingNewContent && editable && (
            <>
              {draggable && (
                <button type="button" className={styles.dragHandle}>
                  <DragIcon />
                </button>
              )}
              <button
                type="button"
                className={styles.menuOptions}
                onClick={() => setShowMenu(!showMenu)}
              >
                <ThreeDots />
              </button>
              {showMenu && (
                <Menu
                  position={PositionType.BottomLeft}
                  menuRef={moreOptionsMenuRef}
                  className={styles.moreOptionsMenu}
                >
                  {editable && contentType === AlbumContentType.write && (
                    <DropDownListItem
                      onClick={() => {
                        setShowMenu(false);
                        setIsEditing(true);
                      }}
                      leftIcon={<EditIcon />}
                    >
                      <span>Edit</span>
                    </DropDownListItem>
                  )}
                  {!isGuestView && (
                    <DropDownListItem
                      onClick={handleDownloadEntry}
                      leftIcon={<LinkIcon />}
                    >
                      <span>Download entry</span>
                    </DropDownListItem>
                  )}
                  {editable && (
                    <DropDownListItem
                      onClick={() => {
                        setShowMenu(false);
                        setShowDeleteContentModal(true);
                      }}
                      leftIcon={<TrashIcon />}
                    >
                      <span className={styles.deleteButton}>Delete</span>
                    </DropDownListItem>
                  )}
                </Menu>
              )}
            </>
          )}
        </div>
      </div>

      {props.isCreatingNewContent ? (
        <>
          {props.contentType === AlbumAddContentTypes.video && (
            <CameraRecord
              onStopRecording={props.onStopRecording}
              title={newContentTitle}
            />
          )}
          {props.contentType === AlbumAddContentTypes.audio && (
            <AudioRecorder
              onStopRecording={props.onStopRecording}
              title={newContentTitle}
            />
          )}
          {props.contentType === AlbumAddContentTypes.write && (
            <RichTextEditor
              onClose={props.onStopRecording}
              title={newContentTitle}
            />
          )}
        </>
      ) : (
        <>
          <div className={styles.contentItem}>
            {contentType === AlbumContentType.video && (
              <video
                src={props.content.mediaUrl}
                poster={props.content.thumbnailUrl}
                controls
                className={styles.videoPlayer}
              >
                <track kind="captions" />
              </video>
            )}
            {contentType === AlbumContentType.audio && (
              <AudioPlayer videoUrl={props.content.mediaUrl} />
            )}
            {contentType === AlbumContentType.image && (
              <img
                src={props.content.mediaUrl}
                alt={props.content.name}
                className={styles.imageContent}
              />
            )}
            {contentType === AlbumContentType.write &&
              (isEditing ? (
                <RichTextEditor
                  value={props.content.content.body}
                  onClose={() => setIsEditing(false)}
                  editId={props.content.content.id}
                  title={props.content.name}
                  onUpdateSuccess={(updatedJournalDocument) => {
                    props.onContentUpdate?.(
                      props.content.id,
                      updatedJournalDocument,
                    );
                  }}
                />
              ) : (
                <HtmlRenderer
                  className={styles.text}
                  html={props.content.content.body}
                />
              ))}
            {contentType === AlbumContentType.document && (
              <Suspense fallback={<Spinner />}>
                <PdfViewer
                  url={props.content.mediaUrl}
                  name={props.content.name}
                />
              </Suspense>
            )}
          </div>
          <div className={styles.contentFooter}>
            {!props.isCreatingNewContent && (
              <div
                className={classnames({
                  [styles.hideNoSpecificDate]: !props.content.createdAt,
                })}
              >
                {editable && (
                  <DatePicker
                    subject="memory"
                    startDate={props.content.startDate}
                    endDate={props.content.endDate}
                    menuPosition={PositionType.BottomRightAligned}
                    onUpdateDate={handleUpdateDate!}
                  />
                )}
              </div>
            )}
            <Button
              buttonSize={ButtonSize.Tiny}
              buttonStyle={ButtonStyle.TertiaryColor}
              onClick={props.onShowViewMode}
            >
              <ExpandIcon />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  if (props.isCreatingNewContent || !draggable || props.index === undefined) {
    return (
      <>
        {contentComponent}
        {!props.isCreatingNewContent &&
          showDeleteContentModal &&
          modalDeleteContent()}
      </>
    );
  }

  return (
    <>
      <Draggable draggableId={props.content.id.toString()} index={props.index}>
        {(provided, snapshot) => (
          <div
            role="listbox"
            key={props.content.id}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            tabIndex={0}
            className={`${styles.draggableContainer} ${
              snapshot.isDragging ? styles.dragging : ''
            }`}
          >
            {contentComponent}
          </div>
        )}
      </Draggable>
      {showDeleteContentModal && modalDeleteContent()}
    </>
  );
};
